import React, { useState, useEffect, useRef } from "react";
import { TabView, TabPanel } from 'primereact/tabview';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import TagControl from './TagControl';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import settings from '../chatSettings.json';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from "primereact/inputtext";
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { fetchAccessToken } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';

export const ChatRequestForm = () => {
    const { getAccessTokenSilently } = useAuth0();
    useEffect(() => { document.title = 'OptiConfig Chat Tool'; }, []);

    const toast = useRef(null);

    const showError = (err) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: err,
                life: 3000
            });
        }
    }

    const [formData, setFormData] = useState({
        vehicleDescription: "",
        equipment: [],
        queries: "",
        model: null,
        vin: "",
        source: "",
        carType: "",
        categories: []
    });

    const [isRunning, setIsRunning] = useState(false);
    const [responseData, setResponseData] = useState(null);
    const [requests, setRequests] = useState([]);
    const [descriptions, setDescriptions] = useState([]);
    const [dialogContent, setDialogContent] = useState("");
    const [dialogVisible, setDialogVisible] = useState(false);
    const [commentsDialogContent, setCommentsDialogContent] = useState({});
    const [commentsDialogVisible, setCommentsDialogVisible] = useState(false);
    const [eqFeedback, setEqFeedback] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]);
    const [requestButtonVisible, setRequestButtonVisible] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setResponseData(null);
    };

    useEffect(() => {
        const savedData = localStorage.getItem("chatRequestForm");

        if (savedData) {
            try {
                const savedObj = JSON.parse(savedData);

                if (savedObj?.vehicleDescription) {
                    if (!savedObj.market) savedObj.market = settings.DefaultMarket;
                    if (!savedObj.language) savedObj.language = settings.DefaultLanguage;

                    if (savedObj.source === "EuroTax") setRequestButtonVisible(true);
                    if (savedObj.source === "DAT") setRequestButtonVisible(true);
                    if (savedObj.source === "AeroPlatform") setRequestButtonVisible(true);
                    if (savedObj.source === "Indicata") setRequestButtonVisible(true);
                    if (savedObj.source === "Partslink24") setRequestButtonVisible(true);
                    if (savedObj.source === "Outvin") setRequestButtonVisible(true);
                    if (savedObj.source === "Jato") setRequestButtonVisible(true);
                    if (savedObj.source === "MyRenault") setRequestButtonVisible(true);
                    if (savedObj.source === "Vincario") setRequestButtonVisible(true);
                    if (savedObj.source === "GPT") setRequestButtonVisible(true);
                    if (savedObj.source === "Autobiz") setRequestButtonVisible(true);
                    if (savedObj.source === "Claude") setRequestButtonVisible(true);

                    setFormData(savedObj);
                }
            } catch {
                setFormData({
                    vehicleDescription: "",
                    equipment: [],
                    categories: settings.Categories,
                    model: settings.Model,
                    queries: settings.Queries.join('\n'),
                    vin: "",
                    source: null,
                    carType: null,
                    market: settings.DefaultMarket,
                    language: settings.DefaultLanguage,
                });
            }
        } else {
            setFormData({
                vehicleDescription: "",
                equipment: [],
                categories: settings.Categories,
                model: settings.Model,
                queries: settings.Queries.join('\n'),
                vin: "",
                source: null,
                carType: null,
                market: settings.DefaultMarket,
                language: settings.DefaultLanguage,
            });
        }
    }, []);

    const handleRequest = async (e) => {
        e.preventDefault();
        setIsRunning(true);

        try {
            const token = await fetchAccessToken(getAccessTokenSilently);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/callApi`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ 
                    source: formData.source, 
                    vin: formData.vin, 
                    market: formData.market,
                    language: formData.language
                }),
            });

            const responseObj = await response.json();

            if (response.status === 500) {
                console.error("ERROR!", responseObj.message, responseObj.details);
                throw responseObj;
            }

            if (responseObj.error)
            {
                showError(responseObj.error);
            }

            if (responseObj.vehicleDescription !== "") {
                handleChange({ target: { name: "vehicleDescription", value: responseObj.vehicleDescription} });
            }
            else {
                handleChange({ target: { name: "vehicleDescription", value: `ERROR: ${responseObj.error ?? "Unable to get data"}`} });
            }
        }
        catch (e) {
            
            showError(e.details || e.message);
        } finally {
            setIsRunning(false);
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsRunning(true);

        localStorage.setItem("chatRequestForm", JSON.stringify(formData));

        let request = {
            text: formData.vehicleDescription,
            equipment: formData.equipment,
            schema: settings.Schema,
            queries: formData.queries.split('\n'),
            model: formData.model,
            vin: formData.vin,
            source: formData.source,
            carType: formData.carType,
            categories: formData.categories
        };

        try {
            const token = await fetchAccessToken(getAccessTokenSilently);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/request`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(request),
            });

            const responseObj = await response.json();

            if (response.status === 500) {
                console.error("ERROR!", responseObj.message, responseObj.details);
                throw responseObj;
            }

            setResponseData(responseObj);

            let reqs = responseObj.results.map(r => ({
                resultNo: r.resultNo,
                file: r.file,
                source: r.source
            }));

            let uniqueReqs = Array.from(new Set(reqs.map(JSON.stringify))).map(JSON.parse);

            setRequests(uniqueReqs);
            setDescriptions(responseObj.descriptions);
        } catch (e) {
            setResponseData(null);
            showError(e.details || e.message);
        } finally {
            setIsRunning(false);
        }
    };

    const isInvalid = () => {
        return (formData?.vehicleDescription?.trim() ?? "") === ""
            || !formData?.equipment?.length
            || !formData?.categories?.length
            || formData?.queries === ""
            || !formData?.carType
            || !formData?.source
            || (formData?.vin?.trim() ?? "") === "";
    }

    const reset2Defaults = () => {
        setFormData({
            vehicleDescription: formData.vehicleDescription,
            equipment: formData.equipment,
            queries: settings.Queries.join('\n'),
            model: settings.Model,
            categories: settings.Categories,
            vin: formData.vin,
            source: formData.source,
            carType: formData.carType,
            market: settings.DefaultMarket,
            language: settings.DefaultLanguage,
        });
    }

    const cleanValues = () => {
        setFormData({
            vehicleDescription: "",
            equipment: [],
            queries: settings.Queries.join('\n'),
            model: settings.Model,
            categories: settings.Categories,
            vin: "",
            source: null,
            carType: null,
            market: settings.DefaultMarket,
            language: settings.DefaultLanguage,
        });

        localStorage.setItem("chatRequestForm", {});
    }

    const sourceOptions = () => {
        return settings.Sources.map(s => { return { "name": s, "value": s }; });
    }

    const carTypeOptions = () => {
        return settings.CarTypes.map(s => { return { "name": s.name, "value": s.name }; });
    }

    const getGroupRes = (equipment) => {
        const exRes = responseData.results.filter(r => r.equipment === equipment).map(r => r.exists.toLowerCase());
        const distinctArray = [...new Set(exRes)];

        let ico = "circle-fill";
        let color = "yellow";
        let tt = "Questionable";

        if (distinctArray.length === 1) {
            tt = `Same result: ${distinctArray[0]}`;
            switch (distinctArray[0].toLowerCase()) {
                case "yes":
                    ico = "circle-fill";
                    color = "green";
                    break;
                case "no":
                    ico = "circle-fill";
                    color = "red";
                    break;
                case "probably not":
                    ico = "circle";
                    color = "red";
                    break;
                case "probably":
                case "probably yes":
                    ico = "circle";
                    color = "green";
                    break;
                default:
                    break;
            }
        }

        return { ico, color, tt };
    }

    const headerTemplate = (data) => {
        const ef = eqFeedback.find(f => f.equipment === data.equipment);

        const { ico, color, tt } = getGroupRes(data.equipment);

        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <span className="font-bold" style={{ width: 290, display: "inline-block" }}>
                    {data.equipment}
                </span>
                <span style={{ width: 80, display: "inline-block", textAlign: "center" }}>
                    {getIcon(ico, color, tt)}
                </span>
                <span>
                    {actionButtons({ resultNo: -1, equipment: data.equipment, like: ef?.like, comment: ef?.comment, override: ef?.override })}
                </span>
                <span>{ef?.comment}</span>
            </span>
        );
    };

    const firstColumn = (row) => {
        return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <span>#{row.resultNo + 1}</span>
        </div>;
    }

    const showDialog = (rNo) => {
        setDialogContent(descriptions[rNo]);
        setDialogVisible(true);
    }

    const showCommentsDialog = (comment, eq, rNo) => {
        setCommentsDialogContent({ comment, eq, rNo });
        setCommentsDialogVisible(true);
    }

    const mark = (like, eq, rNo, override) => {

        if (rNo === -1) {
            const ef = eqFeedback.find(f => f.equipment === eq);
            let fbs = eqFeedback.map(f => {
                if (f.equipment === eq) {
                    f.like = like;
                    f.override = override;
                }

                return f;
            });

            if (!ef) fbs.push({ equipment: eq, like, override });

            setEqFeedback(fbs);

            return;
        }

        let rows = responseData.results.map(row => {

            if (row.equipment === eq && row.resultNo === rNo)
                row.like = like;

            return row;
        });

        setResponseData({ ...responseData, results: rows });
    }

    const saveComment = (contentObj) => {
        if (contentObj.rNo === -1) {
            const ef = eqFeedback.find(f => f.equipment === contentObj.eq);

            let fbs = eqFeedback.map(f => {
                if (f.equipment === contentObj.eq)
                    f.comment = contentObj.comment;

                return f;
            });

            if (!ef) fbs.push({ equipment: contentObj.eq, comment: contentObj.comment });

            setEqFeedback(fbs);
            return;
        }

        let rows = responseData.results.map(row => {

            if (row.equipment === contentObj.eq && row.resultNo === contentObj.rNo)
                row.comment = contentObj.comment;

            return row;
        });

        setResponseData({ ...responseData, results: rows });
    }

    const actionButtons = (row) => {

        return <>
            {row.like !== true && <Button icon="pi pi-thumbs-up" rounded text tooltip="Mark as valid" tooltipOptions={{ position: 'left' }} onClick={() => mark(true, row.equipment, row.resultNo)} />}
            {row.like === true && <Button icon="pi pi-thumbs-up-fill" severity="success" rounded text tooltip="Unmark as valid" tooltipOptions={{ position: 'left' }} onClick={() => mark(undefined, row.equipment, row.resultNo)} />}

            {row.like !== false && <Button icon="pi pi-thumbs-down" rounded text tooltip="Mark as invalid" tooltipOptions={{ position: 'left' }} onClick={() => mark(false, row.equipment, row.resultNo)} />}
            {row.like === false && <Button icon="pi pi-thumbs-down-fill" severity="danger" rounded text tooltip="Unmark as invalid" tooltipOptions={{ position: 'left' }}
                onClick={() => mark(undefined, row.equipment, row.resultNo)} />}

            {(row.like === false && row.resultNo === -1) &&
                <Dropdown
                    value={row.override}
                    style={{ minWidth: 200 }}
                    scrollHeight={400}
                    name="override"
                    onChange={(e) => {
                        console.log(carTypeOptions());
                        mark(false, row.equipment, row.resultNo, e.target.value)
                    }}
                    options={[{ name: "yes", value: "yes" }, { name: "no", value: "no" }, { name: "probably", value: "probably" }, { name: "probably not", value: "probably not" }]}
                    optionLabel="name"
                    placeholder="Select proper result"
                />}

            {!row.comment && <Button icon="pi pi-comment" rounded text tooltip="Comment" tooltipOptions={{ position: 'left' }} onClick={() => showCommentsDialog(row.comment, row.equipment, row.resultNo)} />}
            {row.comment && <Button icon="pi pi-comments" severity="success" rounded text tooltip="Show comment" tooltipOptions={{ position: 'left' }} onClick={() => showCommentsDialog(row.comment, row.equipment, row.resultNo)} />}
        </>;
    }

    const actionsColumn = (row) => {
        return <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {actionButtons(row)}
            <Button icon="pi pi-eye" rounded text tooltip="Show description" tooltipOptions={{ position: 'left' }} onClick={() => showDialog(row.resultNo)} />
        </div>;
    }

    const getIcon = (ico, color, tt) => {
        const cls = `custom-target-icon pi pi-${ico}`;
        return <>
            <Tooltip target=".custom-target-icon" mouseTrack />
            <i className={cls} style={{ color: color, margin: 3 }} data-pr-tooltip={tt}></i>
        </>;
    }

    const exIcon = (row) => {
        const ex = row.exists.toLowerCase();
        switch (ex) {
            case "yes":
                return getIcon("check", "green", ex);
            case "no":
                return getIcon("times", "red", ex);
            case "probably not":
                return getIcon("question", "red", ex);
            case "probably yes":
            case "probably":
                return getIcon("question", "green", ex);
            default:
                return getIcon("exclamation", "gray", ex);
        }
    }

    const removeResult = async (file) => {
        const results = responseData.results.filter(r => r.file !== file);
        setResponseData({ ...responseData, results: results });

        try {
            const token = await fetchAccessToken(getAccessTokenSilently);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/result`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ file }),
            });

            if (response.status !== 200) {
                showError("Unable to delete result");
            }

            setRequests(requests.filter(r => r.file !== file));
        }
        catch (ex) {
            console.error(ex.message);
            showError("Unable to delete result");
        }
    }

    const getExportData = () => {
        const eqs = responseData.results.map(r => {
            const ef = eqFeedback.find(f => f.equipment === r.equipment);

            const { tt } = getGroupRes(r.equipment);

            return {
                equipment: r.equipment,
                resultNo: -1,
                exists: tt,
                like: ef?.like,
                override: ef?.override,
                comment: ef?.comment
            };
        });

        const distinctEqs = [];
        const seen = new Set();

        for (const eq of eqs) {
            if (!seen.has(eq.equipment)) {
                seen.add(eq.equipment);
                distinctEqs.push(eq);
            }
        }

        let results = responseData.results.map(r => r);

        distinctEqs.forEach(eq => {
            results.push(eq);
        });

        const sortedData = results.sort((a, b) => {
            if (a.equipment < b.equipment) return -1;
            if (a.equipment > b.equipment) return 1;
            return a.resultNo - b.resultNo;
        });

        const cleanedData = sortedData.map(d => {
            return {
                Equipment: d.equipment,
                Source: d.source,
                "Detected value": d.exists,
                "Feedback": d.like === true ? "Valid" : (d.like === false) ? "Invalid" : "",
                "Corrected value": d.override,
                Comment: d.comment,
                Category: d.category,
                Quote: d.quote
            };
        });

        return cleanedData;
    }

    const exportJson = () => {
        const cleanedData = getExportData();
        var json = JSON.stringify(cleanedData);

        const fname = `${formData.vin}_export_${new Date().getTime()}.json`;

        const blob = new Blob([json], { type: "application/json" });
        const link = document.createElement("a");

        link.href = URL.createObjectURL(blob);
        link.download = fname;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const exportExcel = () => {
        import('xlsx').then((xlsx) => {

            const cleanedData = getExportData();

            const worksheet = xlsx.utils.json_to_sheet(cleanedData);
            worksheet['!cols'] = [
                { wch: 20 },
                { wch: 20 },
                { wch: 30 },
                { wch: 20 },
                { wch: 20 },
                { wch: 40 },
                { wch: 20 },
                { wch: 100 },
            ];

            const workbook = { Sheets: {}, SheetNames: [formData.vin] };
            workbook.Sheets[formData.vin] = worksheet;

            const excelBuffer = xlsx.write(workbook, {
                bookType: 'xlsx',
                type: 'array'
            });

            saveAsExcelFile(excelBuffer, formData.vin);
        });
    };

    const header = (
        <div className="flex align-items-center justify-content-end gap-2">
            <Button style={{ height: 15 }} type="button" label="Save as Json" icon="pi pi-file" onClick={() => exportJson()} data-pr-tooltip="JSON" />
            <Button style={{ height: 15 }} type="button" label="Save as Excel" icon="pi pi-file-excel" severity="success" onClick={exportExcel} data-pr-tooltip="XLS" />
        </div>
    );

    const saveAsExcelFile = (buffer, fileName) => {
        import('file-saver').then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                let EXCEL_EXTENSION = '.xlsx';
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });

                module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const labelClass = "flex align-items-center justify-content-start h-full pr-2";

    return (
        <TabView>
            <TabPanel header="Request">
                <Toast ref={toast} />

                <div className="grid">
                    <div className="col-12">
                        <div className="grid formgrid p-fluid">

                            {/* VIN Field */}
                            <div className="field col-12 md:col-6 lg:col-4">
                                <div className="grid">
                                    <div className="col-12 md:col-4 lg:col-3">
                                        <label className={labelClass}>VIN:</label>
                                    </div>
                                    <div className="col-12 md:col-8 lg:col-9">
                                        <InputText
                                            name="vin"
                                            value={formData.vin}
                                            onChange={handleChange}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Source Field */}
                            <div className="field col-12 md:col-6 lg:col-4">
                                <div className="grid">
                                    <div className="col-12 md:col-2 lg:col-2">
                                        <label className={labelClass}>Source:</label>
                                    </div>
                                    <div className="col-12 md:col-6 lg:col-6">
                                        <Dropdown
                                            value={formData.source}
                                            name="source"
                                            onChange={(e) => {
                                                handleChange(e);

                                                setRequestButtonVisible(e.value === "EuroTax" || e.value === "AeroPlatform" 
                                                    || e.value === "DAT" || e.value === "Indicata" || e.value === "Partslink24"
                                                    || e.value === "Outvin" || e.value === "Jato" || e.value === "MyRenault" || e.value === "Vincario"
                                                    || e.value === "GPT" || e.value === "Autobiz" || e.value === "Claude" || e.value === "TrackSights");
                                            }}
                                            options={sourceOptions()}
                                            optionLabel="name"
                                            placeholder="Select Source"
                                            className="w-full"
                                            scrollHeight="400"
                                        />
                                    </div>
                                    <div className="col-12 md:col-4 lg:col-4">
                                        {(requestButtonVisible && (formData?.vin ?? "") !== "") && 
                                            <Button
                                                label="Request.." 
                                                onClick={handleRequest}
                                                style={{ maxWidth: 130 }}
                                                />}
                                    </div>
                                </div>
                            </div>

                            {/* Car Type Field */}
                            <div className="field col-12 md:col-6 lg:col-4">
                                <div className="grid">
                                    <div className="col-12 md:col-4 lg:col-4">
                                        <label className={labelClass}>Car Type:</label>
                                    </div>
                                    <div className="col-12 md:col-8 lg:col-8">
                                        <Dropdown
                                            value={formData.carType}
                                            name="carType"
                                            onChange={(e) => {
                                                handleChange(e);

                                                const cType = settings.CarTypes.find(t => t.name === e.value);
                                                setFormData({ ...formData, "carType": e.value, "equipment": cType.equipment });
                                            }}
                                            options={carTypeOptions()}
                                            optionLabel="name"
                                            placeholder="Select Car Type"
                                            className="w-full"
                                            scrollHeight="400"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Equipment Field */}
                            <div className="field col-12">
                                <div className="grid">
                                    <div className="col-12 md:col-2 lg:col-1">
                                        <label className={labelClass}>Equipment:</label>
                                    </div>
                                    <div className="col-12 md:col-10 lg:col-11">
                                        <TagControl 
                                            placeholder="Specify equipment"
                                            tags={formData.equipment}
                                            allowMultiple
                                            onChange={(tags) => {
                                                handleChange({ target: { name: 'equipment', value: tags } });
                                            }} />
                                    </div>
                                </div>
                            </div>

                            {/* Vehicle Description Field */}
                            <div className="field col-12" style={{margin: 0}}>
                                <div className="grid">
                                    <div className="col-12 md:col-2 lg:col-1">
                                        <label className={labelClass}>Vehicle Description:</label>
                                    </div>
                                    <div className="col-12 md:col-10 lg:col-11">
                                        <InputTextarea
                                            name="vehicleDescription"
                                            value={formData.vehicleDescription}
                                            onChange={handleChange}
                                            className="w-full"
                                            rows={10}
                                            autoResize={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {isRunning && <ProgressSpinner />}

                    {!isRunning &&
                        <div className="col-12" style={{ textAlign: "center" }}>
                            <Button link label="Clean" onClick={cleanValues} style={{ width: 150 }} />&nbsp;

                            <Button label="Submit" onClick={handleSubmit} style={{ width: 300 }}
                                disabled={isInvalid()} />

                        </div>
                    }

                    {responseData && <div className="col-12">
                        <Tooltip target=".custom-target-icon" mouseTrack />
                        <div style={{ margin: 5 }}>
                            {requests.map(req => {
                                return <Button
                                    key={req.resultNo}
                                    icon="pi pi-trash"
                                    severity="danger"
                                    aria-label="Remove"
                                    style={{ width: 200, marginRight: 10, height: 15, marginBottom: 5 }}
                                    onClick={() => {
                                        removeResult(req.file);
                                    }}
                                    label={`#${req.resultNo + 1} ${req.source}`} />;
                            })}
                        </div>
                        <DataTable value={responseData.results}
                            header={header}
                            groupRowsBy="equipment"
                            // rowGroupHeaderTemplate={headerTemplate}
                            // rowGroupMode="subheader"
                            // expandableRowGroups
                            // expandedRows={expandedRows}
                            onRowToggle={(e) => setExpandedRows(e.data)}
                            // multiSortMeta={[{ field: "equipment", order: 1 }, { field: "resultNo", order: 1 }]}
                            // sortMode="multiple"
                        >
                            <Column field="equipment" header="Equipment" /*body={firstColumn}*/ style={{ minWidth: 250, maxWidth: 250 }}></Column>
                            <Column field="source" header="Source" style={{ minWidth: 120, maxWidth: 120 }}></Column>
                            <Column field="category" header="Category" style={{ minWidth: 120, maxWidth: 120 }}></Column>
                            <Column body={exIcon} header="Exists" style={{ minWidth: 80, maxWidth: 80 }}></Column>
                            <Column field="quote" header="Feedback / Quote" style={{ width: "100%" }}></Column>
                            <Column header=" " body={actionsColumn}></Column>
                        </DataTable>
                    </div>}

                    {dialogVisible && dialogContent &&
                        <Dialog header="Vehicle description" visible={dialogVisible} style={{ width: '50vw' }}
                            onHide={() => { if (!dialogVisible) return; setDialogVisible(false); }}>
                            {dialogContent}
                        </Dialog>}
                    {commentsDialogVisible &&
                        <Dialog header="Comment" visible={commentsDialogVisible} style={{ width: '50vw', minHeight: '30vh' }}
                            onHide={() => { if (!commentsDialogVisible) return; setCommentsDialogVisible(false); }}>

                            <InputTextarea
                                id="commentsDialogContent"
                                name="commentsDialogContent"
                                value={commentsDialogContent.comment}
                                onChange={(e) => {
                                    setCommentsDialogContent({ ...commentsDialogContent, comment: e.target.value });
                                }}
                                style={{
                                    flex: 1,
                                    resize: "none",
                                    width: "100%",
                                    height: "20vh"
                                }}
                            />

                            <Button label="Save" icon="pi pi-check" onClick={() => {
                                saveComment(commentsDialogContent);
                                setCommentsDialogVisible(false);
                            }} />

                        </Dialog>}
                </div>

            </TabPanel>
            <TabPanel header="Settings">
                <div className="grid">
                    <div className="col-1">
                        <label htmlFor="categories">Categories</label>
                    </div>
                    <div className="col-11">
                        <TagControl
                            placeholder="Specify category"
                            tags={formData.categories ?? []}
                            onChange={(tags) => {
                                handleChange({ target: { name: 'categories', value: tags } });
                            }} />
                    </div>
                    <div className="col-1">
                        <label htmlFor="model">Model</label>
                    </div>
                    <div className="col-11">
                        <Dropdown value={formData.model} name="model" onChange={handleChange} options={[{ name: "gpt-3.5-turbo", value: "gpt-3.5-turbo" }, { name: "gpt-4o-mini", value: "gpt-4o-mini" }, { name: "gpt-4o", value: "gpt-4o" }]} optionLabel="name" placeholder="Select a Model" className="w-full md:w-14rem" />
                    </div>
                    <div className="col-1">
                        <label htmlFor="queries">Prompt</label>
                    </div>
                    <div className="col-11" style={{ minHeight: 400, display: "flex", flexDirection: "column", height: "40vh" }}>
                        <InputTextarea
                            id="queries"
                            name="queries"
                            onChange={handleChange}
                            value={formData.queries}
                            style={{
                                flex: 1,
                                resize: "none",
                                width: "100%",
                                height: "85vh",
                            }}
                        />
                    </div>

                    <div className="col-1">
                    <label htmlFor="">Market</label>
                    </div>
                    <div className="col-11">
                        <Dropdown value={formData.market} name="market" onChange={handleChange} options={settings.Markets.map(m => { return { name: m, value: m }; })} optionLabel="name" placeholder="Select a Market" className="w-full md:w-14rem" />
                    </div>
                    <div className="col-1">
                    <label htmlFor="">Language</label>
                    </div>
                    <div className="col-11">
                        <Dropdown value={formData.language} name="language" onChange={handleChange} options={settings.Languages.map(m => { return { name: m, value: m }; })} optionLabel="name" placeholder="Select a Language" className="w-full md:w-14rem" />
                    </div>
                    <div className="col-8">
                    </div>
                    <div className="col-1"></div>
                    <div className="col-11">
                        <Button label="Reset to defaults" onClick={reset2Defaults} style={{ width: 300 }} />
                    </div>
                </div>
            </TabPanel>
        </TabView>
    );
};

export default ChatRequestForm;